import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { getCities } from '../data/routes';
import { formatDateShort } from '../data/dateLocale';
import { MapPin, Calendar, Users, ChevronDown, X, Search } from 'lucide-react';

const passengerCategories = [
  { id: 'adult', labelKey: 'cat.adult', ageKey: 'cat.adultAge' },
  { id: 'senior', labelKey: 'cat.senior', ageKey: 'cat.seniorAge' },
  { id: 'youth', labelKey: 'cat.youth', ageKey: 'cat.youthAge' },
  { id: 'child', labelKey: 'cat.child', ageKey: 'cat.childAge' },
  { id: 'preschool', labelKey: 'cat.preschool', ageKey: 'cat.preschoolAge' },
];

const SearchForm = ({ onSearchDone, compact = false } = {}) => {
  const navigate = useNavigate();
  const { searchParams, setSearchParams } = useBooking();
  const { t, lang } = useLanguage();
  const cities = getCities(lang);

  // In compact mode, use local draft state that only applies on "ПОИСК"
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (compact) setDraft({ ...searchParams });
  }, [compact]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = compact && draft ? draft : searchParams;
  const updateParams = compact
    ? (updater) => setDraft((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }))
    : setSearchParams;
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [passengersOpen, setPassengersOpen] = useState(false);
  const [depDateOpen, setDepDateOpen] = useState(false);
  const [retDateOpen, setRetDateOpen] = useState(false);
  const [fromQuery, setFromQuery] = useState('');
  const [toQuery, setToQuery] = useState('');
  const fromRef = useRef(null);
  const fromRef2 = useRef(null);
  const toRef = useRef(null);
  const toRef2 = useRef(null);
  const passRef = useRef(null);
  const passRef2 = useRef(null);
  const depCalRef = useRef(null);
  const retCalRef = useRef(null);
  const mobDepCalRef = useRef(null);
  const mobRetCalRef = useRef(null);

  const p = params.passengers;
  const passengerTotal = p.adult + p.senior + p.youth + p.child + p.preschool;

  useEffect(() => {
    const handler = (e) => {
      const inRef = (ref) => ref.current && ref.current.contains(e.target);
      if (!inRef(fromRef) && !inRef(fromRef2)) setFromOpen(false);
      if (!inRef(toRef) && !inRef(toRef2)) setToOpen(false);
      if (!inRef(passRef) && !inRef(passRef2)) setPassengersOpen(false);
      if (!inRef(depCalRef) && !inRef(mobDepCalRef)) setDepDateOpen(false);
      if (!inRef(retCalRef) && !inRef(mobRetCalRef)) setRetDateOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredFrom = cities.filter((c) =>
    c.name.toLowerCase().includes(fromQuery.toLowerCase()) && c.name !== params.to
  );
  const filteredTo = cities.filter((c) =>
    c.name.toLowerCase().includes(toQuery.toLowerCase()) && c.name !== params.from
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (!params.from || !params.to || !params.departureDate) return;
    if (params.from === params.to) return;
    closeAll();
    if (compact && draft) {
      // Apply draft changes to real searchParams
      setSearchParams({ ...draft, _ts: Date.now() });
    }
    if (onSearchDone) onSearchDone();
    if (window.location.pathname === '/search') {
      if (!compact) setSearchParams((p) => ({ ...p, _ts: Date.now() }));
    } else {
      navigate('/search');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const closeAll = () => { setFromOpen(false); setToOpen(false); setPassengersOpen(false); setDepDateOpen(false); setRetDateOpen(false); };

  /* ── Mini Calendar Component ── */
  const MiniCalendar = ({ value, minDate, onSelect }) => {
    const selected = value ? new Date(value + 'T00:00:00') : null;
    const minD = minDate ? new Date(minDate + 'T00:00:00') : new Date();
    const [viewDate, setViewDate] = useState(() => selected || new Date());

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDay = firstDay === 0 ? 6 : firstDay - 1; // Monday first

    const monthLabels = {
      ru: ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'],
      en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
      lv: ['Janvāris','Februāris','Marts','Aprīlis','Maijs','Jūnijs','Jūlijs','Augusts','Septembris','Oktobris','Novembris','Decembris'],
      lt: ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'],
      pl: ['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'],
    };
    const dayLabels = {
      ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
      en: ['Mo','Tu','We','Th','Fr','Sa','Su'],
      lv: ['Pr','Ot','Tr','Ce','Pk','Se','Sv'],
      lt: ['Pr','An','Tr','Ke','Pe','Še','Se'],
      pl: ['Pn','Wt','Śr','Cz','Pt','So','Nd'],
    };

    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    const isDisabled = (d) => {
      const date = new Date(year, month, d);
      return date < new Date(minD.getFullYear(), minD.getMonth(), minD.getDate());
    };
    const isSelected = (d) => {
      if (!selected) return false;
      return selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === d;
    };
    const isToday = (d) => {
      const now = new Date();
      return now.getFullYear() === year && now.getMonth() === month && now.getDate() === d;
    };

    return (
      <div className="p-3 w-[280px]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}
            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-[#484c55]/60">‹</button>
          <span className="text-sm font-semibold">{(monthLabels[lang] || monthLabels.en)[month]} {year}</span>
          <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}
            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-[#484c55]/60">›</button>
        </div>
        <div className="grid grid-cols-7 gap-0 mb-1">
          {(dayLabels[lang] || dayLabels.en).map((d) => (
            <div key={d} className="text-[10px] text-center text-[#484c55]/40 font-medium py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0">
          {days.map((d, i) => d === null ? (
            <div key={`e${i}`} />
          ) : (
            <button key={d} type="button" disabled={isDisabled(d)}
              onClick={() => {
                const mm = String(month + 1).padStart(2, '0');
                const dd = String(d).padStart(2, '0');
                onSelect(`${year}-${mm}-${dd}`);
              }}
              className={`w-9 h-9 rounded-full text-xs flex items-center justify-center transition-colors
                ${isDisabled(d) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-[#c30909]/10 cursor-pointer'}
                ${isSelected(d) ? 'bg-[#c30909] text-white hover:bg-[#a00707]' : ''}
                ${isToday(d) && !isSelected(d) ? 'border border-[#c30909] text-[#c30909]' : ''}
              `}>
              {d}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSearch} className="w-full">
      {/* ===== DESKTOP LAYOUT ===== */}
      <div className="hidden lg:block">
        <div className="flex items-stretch gap-2">
        <div className="bg-white rounded shadow-lg flex items-stretch flex-1 min-w-0 border border-gray-200">
          {/* From */}
          <div className="relative flex-1 min-w-0" ref={fromRef2}>
            <div
              className="px-5 py-2.5 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => { setFromOpen(!fromOpen); setToOpen(false); setPassengersOpen(false); setDepDateOpen(false); setRetDateOpen(false); }}
            >
              <p className="text-[13px] text-[#888] tracking-wide mb-1">{t('search.from')}</p>
              <p className="text-[16px] font-medium truncate text-[#222]">
                {params.from || <span className="text-[#9ca3af]">{t('search.enterCity')}</span>}
              </p>
            </div>
            {fromOpen && (
              <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-50 max-h-[280px] overflow-auto w-[260px]">
                <div className="p-2">
                  <input autoFocus value={fromQuery} onChange={(e) => setFromQuery(e.target.value)}
                    placeholder={t('search.searchCity')}
                    className="w-full px-3 py-2 text-sm border rounded outline-none focus:border-[#c30909]" />
                </div>
                {filteredFrom.map((city) => (
                  <div key={city.id} className="px-4 py-2.5 hover:bg-[#f0f0f1] cursor-pointer transition-colors"
                    onClick={() => { updateParams((p) => ({ ...p, from: city.name })); setFromOpen(false); setFromQuery(''); }}>
                    <p className="text-sm font-medium">{city.name}</p>
                    <p className="text-[10px] text-[#484c55]/50">{city.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* To */}
          <div className="relative flex-1 min-w-0" ref={toRef2}>
            <div
              className="px-5 py-2.5 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => { setToOpen(!toOpen); setFromOpen(false); setPassengersOpen(false); setDepDateOpen(false); setRetDateOpen(false); }}
            >
              <p className="text-[13px] text-[#888] tracking-wide mb-1">{t('search.to')}</p>
              <p className="text-[16px] font-medium truncate text-[#222]">
                {params.to || <span className="text-[#9ca3af]">{t('search.enterCity')}</span>}
              </p>
            </div>
            {toOpen && (
              <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-50 max-h-[280px] overflow-auto w-[260px]">
                <div className="p-2">
                  <input autoFocus value={toQuery} onChange={(e) => setToQuery(e.target.value)}
                    placeholder={t('search.searchCity')}
                    className="w-full px-3 py-2 text-sm border rounded outline-none focus:border-[#c30909]" />
                </div>
                {filteredTo.map((city) => (
                  <div key={city.id} className="px-4 py-2.5 hover:bg-[#f0f0f1] cursor-pointer transition-colors"
                    onClick={() => { updateParams((p) => ({ ...p, to: city.name })); setToOpen(false); setToQuery(''); }}>
                    <p className="text-sm font-medium">{city.name}</p>
                    <p className="text-[10px] text-[#484c55]/50">{city.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Departure date */}
          <div className="relative flex-1 min-w-0" ref={depCalRef}>
            <div
              className="px-5 py-2.5 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors h-full"
              onClick={() => { setDepDateOpen(!depDateOpen); setRetDateOpen(false); setFromOpen(false); setToOpen(false); setPassengersOpen(false); }}
            >
              <p className="text-[13px] text-[#888] tracking-wide mb-1">{t('search.departureDate')}</p>
              <p className="text-[16px] font-medium whitespace-nowrap text-[#222]">
                {params.departureDate ? formatDateShort(params.departureDate, lang) : t('search.selectDate')}
              </p>
            </div>
            {depDateOpen && (
              <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-50">
                <MiniCalendar value={params.departureDate} minDate={today}
                  onSelect={(d) => { updateParams((p) => ({ ...p, departureDate: d })); setDepDateOpen(false); }} />
              </div>
            )}
          </div>

          {/* Return date */}
          <div className="relative flex-1 min-w-0" ref={retCalRef}>
            <div
              className="px-5 py-2.5 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors h-full"
              onClick={() => { setRetDateOpen(!retDateOpen); setDepDateOpen(false); setFromOpen(false); setToOpen(false); setPassengersOpen(false); }}
            >
              <p className="text-[13px] text-[#888] tracking-wide mb-1">{t('search.returnDate')}</p>
              <p className={`text-[16px] font-medium whitespace-nowrap ${params.returnDate ? 'text-[#222]' : 'text-[#9ca3af]'}`}>
                {params.returnDate ? formatDateShort(params.returnDate, lang) : t('search.selectDate')}
              </p>
            </div>
            {retDateOpen && (
              <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-50">
                <MiniCalendar value={params.returnDate} minDate={params.departureDate || today}
                  onSelect={(d) => { updateParams((p) => ({ ...p, returnDate: d })); setRetDateOpen(false); }} />
              </div>
            )}
          </div>

          {/* Passengers */}
          <div className="relative" ref={passRef2}>
            <div
              className="px-5 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors min-w-[130px] flex items-center gap-2"
              onClick={() => { setPassengersOpen(!passengersOpen); setFromOpen(false); setToOpen(false); setDepDateOpen(false); setRetDateOpen(false); }}
            >
              <Users size={16} className="text-[#c09a3a] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[13px] text-[#888] tracking-wide mb-1">{t('search.passengers')}</p>
                <p className="text-[16px] font-medium text-[#222]">{passengerTotal} {t('search.addPassenger')}</p>
              </div>
            </div>
            {passengersOpen && (
              <div className="absolute top-full left-0 right-0 bg-white rounded-lg shadow-xl border mt-1 z-50 p-4 min-w-[280px]">
                {passengerCategories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium">{t(cat.labelKey)}</p>
                      <p className="text-[10px] text-[#484c55]/50">{t(cat.ageKey)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button"
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-sm hover:bg-gray-50 disabled:opacity-30 text-[#c30909]"
                        disabled={params.passengers[cat.id] <= 0 || (passengerTotal <= 1 && params.passengers[cat.id] === 1)}
                        onClick={() => updateParams((p) => ({ ...p, passengers: { ...p.passengers, [cat.id]: Math.max(0, p.passengers[cat.id] - 1) } }))}>
                        −
                      </button>
                      <span className="text-sm font-semibold w-4 text-center">{params.passengers[cat.id]}</span>
                      <button type="button"
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-sm hover:bg-gray-50 disabled:opacity-30 text-[#2e9e5a]"
                        disabled={passengerTotal >= 9}
                        onClick={() => updateParams((p) => ({ ...p, passengers: { ...p.passengers, [cat.id]: Math.min(9, p.passengers[cat.id] + 1) } }))}>
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          </div>
          {/* Search button - separate */}
          <button type="submit"
            className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-[15px] uppercase tracking-wider px-10 py-3 rounded transition-colors flex items-center justify-center flex-shrink-0">
            {t('search.searchBtn')}
          </button>
        </div>
      </div>

      {/* ===== MOBILE LAYOUT ===== */}
      <div className="lg:hidden">
        <div className="bg-white rounded-xl shadow-xl">
          {/* From */}
          <div className="relative" ref={fromRef}>
            <div
              className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer"
              onClick={() => { setFromOpen(!fromOpen); setToOpen(false); setPassengersOpen(false); }}
            >
              <MapPin size={18} className="text-[#c30909] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#484c55]/50 font-medium">{t('search.from')}</p>
                <p className="text-base font-semibold truncate">
                  {params.from || <span className="text-[#484c55]/30 font-normal">{t('search.selectCity')}</span>}
                </p>
              </div>
              {params.from && (
                <X size={16} className="text-[#484c55]/30"
                  onClick={(e) => { e.stopPropagation(); updateParams((p) => ({ ...p, from: '' })); }} />
              )}
            </div>
            {fromOpen && (
              <div className="absolute top-full left-0 right-0 bg-white shadow-xl border-t z-50 max-h-[250px] overflow-auto">
                <div className="p-2">
                  <input autoFocus value={fromQuery} onChange={(e) => setFromQuery(e.target.value)}
                    placeholder={t('search.searchCity')}
                    className="w-full px-3 py-2 text-sm border rounded outline-none focus:border-[#c30909]" />
                </div>
                {filteredFrom.map((city) => (
                  <div key={city.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50"
                    onClick={() => { updateParams((p) => ({ ...p, from: city.name })); setFromOpen(false); setFromQuery(''); }}>
                    <p className="text-sm font-medium">{city.name}</p>
                    <p className="text-[10px] text-[#484c55]/50">{city.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* To */}
          <div className="relative" ref={toRef}>
            <div
              className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer"
              onClick={() => { setToOpen(!toOpen); setFromOpen(false); setPassengersOpen(false); }}
            >
              <MapPin size={18} className="text-[#c30909] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#484c55]/50 font-medium">{t('search.to')}</p>
                <p className="text-base font-semibold truncate">
                  {params.to || <span className="text-[#484c55]/30 font-normal">{t('search.selectCity')}</span>}
                </p>
              </div>
              {params.to && (
                <X size={16} className="text-[#484c55]/30"
                  onClick={(e) => { e.stopPropagation(); updateParams((p) => ({ ...p, to: '' })); }} />
              )}
            </div>
            {toOpen && (
              <div className="absolute top-full left-0 right-0 bg-white shadow-xl border-t z-50 max-h-[250px] overflow-auto">
                <div className="p-2">
                  <input autoFocus value={toQuery} onChange={(e) => setToQuery(e.target.value)}
                    placeholder={t('search.searchCity')}
                    className="w-full px-3 py-2 text-sm border rounded outline-none focus:border-[#c30909]" />
                </div>
                {filteredTo.map((city) => (
                  <div key={city.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50"
                    onClick={() => { updateParams((p) => ({ ...p, to: city.name })); setToOpen(false); setToQuery(''); }}>
                    <p className="text-sm font-medium">{city.name}</p>
                    <p className="text-[10px] text-[#484c55]/50">{city.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dates row */}
          <div className="flex border-b border-gray-100">
            {/* Departure date */}
            <div className="flex-1 relative" ref={mobDepCalRef}>
              <div className="flex items-center gap-2 px-3 py-3 border-r border-gray-100 cursor-pointer"
                onClick={() => { setDepDateOpen(!depDateOpen); setRetDateOpen(false); setFromOpen(false); setToOpen(false); setPassengersOpen(false); }}>
                <Calendar size={16} className="text-[#c30909] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-[#484c55]/50 font-medium">{t('search.departureDateShort')}</p>
                  <p className="text-[15px] font-semibold text-[#484c55]">
                    {params.departureDate ? formatDateShort(params.departureDate, lang) : t('search.selectDate')}
                  </p>
                </div>
              </div>
              {depDateOpen && (
                <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-[80]">
                  <MiniCalendar value={params.departureDate} minDate={today}
                    onSelect={(d) => { updateParams((p) => ({ ...p, departureDate: d })); setDepDateOpen(false); }} />
                </div>
              )}
            </div>

            {/* Return date */}
            <div className="flex-1 relative" ref={mobRetCalRef}>
              <div className="flex items-center gap-2 px-3 py-3 cursor-pointer"
                onClick={() => { setRetDateOpen(!retDateOpen); setDepDateOpen(false); setFromOpen(false); setToOpen(false); setPassengersOpen(false); }}>
                <Calendar size={16} className="text-[#c30909] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-[#484c55]/50 font-medium">{t('search.returnDateShort')}</p>
                  <p className={`text-[15px] font-semibold ${params.returnDate ? 'text-[#484c55]' : 'text-[#484c55]/30 font-normal'}`}>
                    {params.returnDate ? formatDateShort(params.returnDate, lang) : t('search.selectDate')}
                  </p>
                </div>
                {params.returnDate && (
                  <X size={14} className="text-[#484c55]/40"
                    onClick={(e) => { e.stopPropagation(); updateParams((p) => ({ ...p, returnDate: '' })); }} />
                )}
              </div>
              {retDateOpen && (
                <div className="absolute top-full right-0 bg-white rounded-lg shadow-xl border mt-1 z-[80]">
                  <MiniCalendar value={params.returnDate} minDate={params.departureDate || today}
                    onSelect={(d) => { updateParams((p) => ({ ...p, returnDate: d })); setRetDateOpen(false); }} />
                </div>
              )}
            </div>
          </div>

          {/* Passengers */}
          <div className="relative" ref={passRef}>
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer"
              onClick={() => { setPassengersOpen(!passengersOpen); setFromOpen(false); setToOpen(false); }}
            >
              <Users size={18} className="text-[#c30909] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-[#484c55]/50 font-medium">{t('search.passengers')}</p>
                <p className="text-base font-semibold">{passengerTotal} {t('search.addPassenger')}</p>
              </div>
              <ChevronDown size={16} className="text-[#484c55]/40" />
            </div>
            {passengersOpen && (
              <div className="border-t border-gray-100 px-4 pb-3">
                {passengerCategories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between py-2.5">
                    <div>
                      <p className="text-sm font-medium">{t(cat.labelKey)}</p>
                      <p className="text-[10px] text-[#484c55]/50">{t(cat.ageKey)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button"
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-lg hover:bg-gray-50 disabled:opacity-30 text-[#c30909]"
                        disabled={params.passengers[cat.id] <= 0 || (passengerTotal <= 1 && params.passengers[cat.id] === 1)}
                        onClick={() => updateParams((p) => ({ ...p, passengers: { ...p.passengers, [cat.id]: Math.max(0, p.passengers[cat.id] - 1) } }))}>
                        −
                      </button>
                      <span className="text-base font-semibold w-5 text-center">{params.passengers[cat.id]}</span>
                      <button type="button"
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-lg hover:bg-gray-50 disabled:opacity-30 text-[#2e9e5a]"
                        disabled={passengerTotal >= 9}
                        onClick={() => updateParams((p) => ({ ...p, passengers: { ...p.passengers, [cat.id]: Math.min(9, p.passengers[cat.id] + 1) } }))}>
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search button */}
          <div className="p-3 pt-0">
            <button type="submit"
              className="w-full bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-base uppercase tracking-wider py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Search size={18} />
              {t('search.searchBtn')}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SearchForm;
